/**
 * Download an authenticated binary asset (PDF, CSV) via fetch → blob → trigger
 * browser download. Uses the same auth-header logic as apiClient but streams
 * the response body as a Blob rather than parsing JSON.
 *
 * Usage:
 *   import { downloadAuthFile } from "@/lib/downloadFile";
 *   await downloadAuthFile("/reports/abc123/pdf");
 */
import { apiClient } from "@/lib/apiClient";

/**
 * Trigger a browser download of an authenticated file endpoint.
 * @param {string} path — path under /api (e.g. "/reports/abc/pdf")
 * @param {string} [fallbackName] — filename to use if server didn't send Content-Disposition
 * @returns {Promise<void>}
 */
export async function downloadAuthFile(path, fallbackName) {
  const res = await apiClient.get(path, { responseType: "blob" });
  const cd = res.headers?.["content-disposition"] || "";
  const match = /filename="?([^"]+)"?/.exec(cd);
  const filename = (match && match[1]) || fallbackName || "download";
  const blob = res.data;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Trigger a browser download of a PUBLIC (unauth) file endpoint. Used by
 * the demo shell so visitors can download real PDFs of stubbed data
 * without needing an account (lead-gen).
 */
export async function downloadPublicFile(url, fallbackName) {
  const res = await fetch(url, { method: "GET" });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const cd = res.headers.get("content-disposition") || "";
  const match = /filename="?([^"]+)"?/.exec(cd);
  const filename = (match && match[1]) || fallbackName || "download";
  const blob = await res.blob();
  const obj = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = obj;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(obj);
}
